import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { countryCodesList } from '../mock';
import { toast } from 'sonner';
import { fetchGeoLocation, submitLead, handleApiResponse } from '../services/apiService';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    subject: '',
    message: ''
  });
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadGeoData = async () => {
      const data = await fetchGeoLocation();
      setGeoData(data);
      if (data?.data?.countryCode) {
        const countryItem = countryCodesList.find(c => c.country === data.data.countryName);
        if (countryItem) {
          setFormData(prev => ({ ...prev, countryCode: countryItem.code }));
        }
      }
    };
    loadGeoData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await submitLead(formData, geoData);
      const result = handleApiResponse(response);
      
      if (result.type === 'redirect') {
        toast.success(result.message);
        navigate(result.url);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
      setFormData({ name: '', email: '', countryCode: '+1', phone: '', subject: '', message: '' });
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Contact Trademark Inspire - Get Expert Help with Your Trademark | Call +1 (888) 2029 517</title>
        <meta name="description" content="Contact Trademark Inspire for expert trademark assistance. Email: Info@trademarkinspire.com | Phone: +1 (888) 2029 517 | Address: 1652 W Texas St, Fairfield, CA 94533. Get free consultation today!" />
        <meta name="keywords" content="contact trademark attorney, trademark consultation, trademark help, trademark questions, contact us, trademark support" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-gray-100">
              Have questions? We're here to help. Reach out to our team today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-[#093336] mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6 border-2 border-gray-200 rounded-2xl p-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your.email@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                    <div className="flex gap-2">
                      <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodesList.map((item) => (
                            <SelectItem key={item.id} value={item.code}>
                              {item.code} {item.country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFormData({ ...formData, phone: value });
                        }}
                        placeholder="Phone number"
                        className="flex-1"
                        pattern="[0-9]{10}"
                        title="Please enter a valid 10-digit phone number"
                        maxLength={10}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <Input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="What is this regarding?"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                    <Textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more about your inquiry..."
                      rows={6}
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-[#093336] hover:bg-[#0a4548] text-white py-6 text-lg disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-3xl font-bold text-[#093336] mb-6">Get In Touch</h2>
                <p className="text-gray-700 mb-8">
                  We're here to answer any questions you may have about our trademark and legal services. Reach out to us and we'll respond as soon as we can.
                </p>
                
                <div className="space-y-6">
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-[#093336] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#093336] mb-1">Email Us</h3>
                      <a href="mailto:Info@trademarkinspire.com" className="text-gray-700 hover:text-[#F77F00] transition-colors">
                        Info@trademarkinspire.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-[#093336] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#093336] mb-1">Call Us</h3>
                      <a href="tel:+18882029517" className="text-gray-700 hover:text-[#F77F00] transition-colors">
                        +1 (888) 2029 517
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-[#093336] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#093336] mb-1">Visit Us</h3>
                      <p className="text-gray-700">1652 W Texas St, Fairfield, CA 94533</p>
                      <p className="text-gray-700">United States</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-[#093336] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#093336] mb-1">Business Hours</h3>
                      <p className="text-gray-700">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-700">Saturday: 10:00 AM - 4:00 PM</p>
                      <p className="text-gray-700">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
